import { useCallback } from "react";
import { useModelStore } from "../store/useModelStore";
import { useCursorManager } from "@/components/menu/hooks/useCursorManager";
import { useGlobalSelectionStore } from "../store/useGlobalSelectionStore";

export const useClickToSelect = () => {
  const modelStore = useModelStore();
  const { activeCursor } = useCursorManager();
  const activeCursorMode = useGlobalSelectionStore(
    (state) => state.activeCursorMode
  );

  const getSelectionFilter = useCallback(():
    | "all"
    | "nodes"
    | "beams"
    | null => {
    return (window as any).__currentSelectionMode || null;
  }, []);

  const isTableApplyByClickActive = useCallback((): boolean => {
    const globalFlag = (window as any).__applyByClickActive || false;
    const hasCursorMode = !!activeCursor;

    // Table apply-by-click modes (these DISABLE box selection)
    const isTableMode =
      activeCursorMode === "loadcase-apply-click" ||
      activeCursorMode === "property-apply-click" ||
      activeCursorMode === "support-apply-click";

    return globalFlag || hasCursorMode || isTableMode;
  }, [activeCursor, activeCursorMode]);

  const isBoxSelectionEnabled = useCallback((): boolean => {
    return !isTableApplyByClickActive();
  }, [isTableApplyByClickActive]);

  const handleNodeClick = useCallback(
    (nodeId: string, event: any) => {
      const applyByClickActive = (window as any).__applyByClickActive || false;

      if (event?.stopPropagation) {
        event.stopPropagation();
      }

      // Check if TABLE apply-by-click mode is active
      const isTableMode =
        activeCursorMode === "loadcase-apply-click" ||
        activeCursorMode === "property-apply-click" ||
        activeCursorMode === "support-apply-click";

      if (activeCursor || applyByClickActive || isTableMode) {
        window.dispatchEvent(
          new CustomEvent("applyByClickNode", {
            detail: { nodeId },
          })
        );
        return;
      }

      // Normal selection logic
      const filter = getSelectionFilter() || "all";

      if (filter === "beams") {
        return; // Ignore node clicks in beams-only mode
      }

      // ALWAYS use additive selection - no clearing
      const isAlreadySelected = modelStore.selectedNodeIds.includes(nodeId);
      const currentNodes = modelStore.selectedNodeIds;
      const currentBeams = modelStore.selectedBeamIds;

      let newNodeIds: string[];
      if (isAlreadySelected) {
        // Toggle off
        newNodeIds = currentNodes.filter((id) => id !== nodeId);
      } else {
        // Add to selection
        newNodeIds = [...currentNodes, nodeId];
      }

      useModelStore.setState((state: any) => ({
        selectedNodeIds: newNodeIds,
        selectedBeamIds: currentBeams,
        nodes: state.nodes.map((node: any) => ({
          ...node,
          isSelected: newNodeIds.includes(node.id),
          color: newNodeIds.includes(node.id) ? "#ff8c00" : "#000000",
        })),
        beams: state.beams.map((beam: any) => ({
          ...beam,
          isSelected: currentBeams.includes(beam.id),
        })),
      }));
    },
    [modelStore, getSelectionFilter, activeCursor, activeCursorMode]
  );

  const handleBeamClick = useCallback(
    (beamId: string, event: any) => {
      const applyByClickActive = (window as any).__applyByClickActive || false;

      if (event?.stopPropagation) {
        event.stopPropagation();
      }

      const isTableMode =
        activeCursorMode === "loadcase-apply-click" ||
        activeCursorMode === "property-apply-click" ||
        activeCursorMode === "support-apply-click";

      if (activeCursor || applyByClickActive || isTableMode) {
        window.dispatchEvent(
          new CustomEvent("applyByClickBeam", {
            detail: { beamId },
          })
        );
        return;
      }

      const filter = getSelectionFilter() || "all";

      if (filter === "nodes") {
        return; // Ignore beam clicks in nodes-only mode
      }

      // ALWAYS use additive selection - no clearing
      const isAlreadySelected = modelStore.selectedBeamIds.includes(beamId);
      const currentNodes = modelStore.selectedNodeIds;
      const currentBeams = modelStore.selectedBeamIds;

      let newBeamIds: string[];
      if (isAlreadySelected) {
        // Toggle off
        newBeamIds = currentBeams.filter((id) => id !== beamId);
      } else {
        // Add to selection
        newBeamIds = [...currentBeams, beamId];
      }

      // Calculate node selection based on filter
      let newNodeIds = currentNodes;
      if (filter === "all") {
        // Add beam endpoints
        const beamEndpointNodes = new Set<string>();
        newBeamIds.forEach((bid) => {
          const beam = modelStore.beams.find((b) => b.id === bid);
          if (beam) {
            beamEndpointNodes.add(beam.startNodeId);
            beamEndpointNodes.add(beam.endNodeId);
          }
        });
        newNodeIds = [
          ...new Set([...currentNodes, ...Array.from(beamEndpointNodes)]),
        ];
      }

      useModelStore.setState((state: any) => ({
        selectedBeamIds: newBeamIds,
        selectedNodeIds: newNodeIds,
        beams: state.beams.map((beam: any) => ({
          ...beam,
          isSelected: newBeamIds.includes(beam.id),
        })),
        nodes: state.nodes.map((node: any) => ({
          ...node,
          isSelected: newNodeIds.includes(node.id),
          color: newNodeIds.includes(node.id) ? "#ff8c00" : "#000000",
        })),
      }));
    },
    [modelStore, getSelectionFilter, activeCursor, activeCursorMode]
  );

  return {
    handleNodeClick,
    handleBeamClick,
    isBoxSelectionEnabled,
    isTableApplyByClickActive,
    getSelectionFilter,
  };
};
